import type { ReactNode } from "react";
import { useState } from "react";
import { ArrowLeftRight, ChevronDown, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import type { AuditEvent, AuditCategory, AuditFieldChange } from "../types";
import {
  actionDotColor,
  formatEntityType,
  formatIp,
  formatModule,
  formatTime,
  initialsOf,
  relativeTime,
  SEVERITY_DOT_COLOR,
  SEVERITY_LABEL,
  truncate,
} from "../lib/format";

const CARD_BORDER = "hsl(210,30%,85%)";

const CATEGORY_STYLES: Record<
  AuditCategory,
  { label: string; bg: string; fg: string }
> = {
  SYSTEM_ACTIVITY: {
    label: "System Activity",
    bg: "hsl(210,55%,92%)",
    fg: "hsl(210,100%,28%)",
  },
  DATA_CHANGE: {
    label: "Data Change",
    bg: "hsl(262,55%,92%)",
    fg: "hsl(262,83%,38%)",
  },
  SECURITY_EVENT: {
    label: "Security Event",
    bg: "hsl(38,92%,92%)",
    fg: "hsl(38,92%,30%)",
  },
  SYSTEM_EVENT: {
    label: "System Event",
    bg: "hsl(210,16%,92%)",
    fg: "hsl(215,16%,40%)",
  },
};

const STATUS_BADGE_COLORS: Record<
  string,
  { bg: string; fg: string }
> = {
  ACTIVE: { bg: "hsl(142,55%,92%)", fg: "hsl(142,72%,28%)" },
  INACTIVE: { bg: "hsl(210,16%,92%)", fg: "hsl(215,16%,40%)" },
  SUSPENDED: { bg: "hsl(0,84%,95%)", fg: "hsl(0,72%,40%)" },
  OPEN: { bg: "hsl(210,55%,92%)", fg: "hsl(210,100%,28%)" },
  COMPLETED: { bg: "hsl(142,55%,92%)", fg: "hsl(142,72%,28%)" },
  CANCELLED: { bg: "hsl(210,16%,92%)", fg: "hsl(215,16%,40%)" },
  SUCCESS: { bg: "hsl(142,55%,92%)", fg: "hsl(142,72%,28%)" },
  FAILURE: { bg: "hsl(0,84%,95%)", fg: "hsl(0,72%,40%)" },
  WARNING: { bg: "hsl(38,92%,92%)", fg: "hsl(38,92%,30%)" },
};

function formatFieldName(field: string): string {
  const titleCase = (segment: string): string => {
    const spaced = segment
      .replace(/([A-Z])/g, " $1")
      .replace(/[_-]/g, " ")
      .trim()
      .toLowerCase();
    if (!spaced) return segment;
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
  };
  return field.split(".").map(titleCase).join(" → ");
}

function formatAuditValue(val: unknown): ReactNode {
  if (val === null || val === undefined || val === "") {
    return <span className="text-muted-foreground">—</span>;
  }
  if (typeof val === "boolean") {
    return val ? (
      <span
        className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium"
        style={{ background: "hsl(142,55%,92%)", color: "hsl(142,72%,28%)" }}
      >
        Yes
      </span>
    ) : (
      <span
        className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium"
        style={{ background: "hsl(0,84%,95%)", color: "hsl(0,72%,40%)" }}
      >
        No
      </span>
    );
  }
  if (Array.isArray(val)) {
    if (val.length === 0) {
      return <span className="text-muted-foreground">—</span>;
    }
    return <span>{val.join(", ")}</span>;
  }
  if (typeof val === "string") {
    const badge = STATUS_BADGE_COLORS[val.toUpperCase()];
    if (badge) {
      return (
        <span
          className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium"
          style={{ background: badge.bg, color: badge.fg }}
        >
          {val}
        </span>
      );
    }
    return <span>{val}</span>;
  }
  if (typeof val === "number") return <span>{String(val)}</span>;
  try {
    return <span className="font-mono text-[11px]">{JSON.stringify(val)}</span>;
  } catch {
    return <span className="text-muted-foreground">[object]</span>;
  }
}

function valuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}

const MAX_VISIBLE_CHANGES = 10;

function FieldChangesTable({ changes }: { changes: AuditFieldChange[] }) {
  const [showAll, setShowAll] = useState(false);
  const visible =
    showAll || changes.length <= MAX_VISIBLE_CHANGES
      ? changes
      : changes.slice(0, MAX_VISIBLE_CHANGES);
  const hiddenCount = changes.length - visible.length;

  return (
    <div className="mt-3">
      <div className="mb-2 flex items-center gap-1.5 text-[12px] font-semibold text-muted-foreground">
        <ArrowLeftRight className="h-3 w-3" strokeWidth={1.75} />
        <span>Field changes</span>
      </div>
      <div
        className="overflow-hidden rounded-lg border"
        style={{ borderColor: CARD_BORDER }}
      >
        <table className="w-full text-[12px]">
          <thead>
            <tr style={{ background: "hsl(210,30%,97%)" }}>
              <th className="px-3 py-1.5 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Field
              </th>
              <th className="px-3 py-1.5 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Old value
              </th>
              <th className="px-3 py-1.5 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                New value
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {visible.map((fc) => {
              const changed = !valuesEqual(fc.before, fc.after);
              return (
                <tr key={fc.field}>
                  <td className="px-3 py-1.5 text-[12px]">
                    {formatFieldName(fc.field)}
                  </td>
                  <td className="px-3 py-1.5 text-muted-foreground">
                    {formatAuditValue(fc.before)}
                  </td>
                  <td
                    className="px-3 py-1.5"
                    style={
                      changed
                        ? { background: "hsl(173,50%,96%)" }
                        : undefined
                    }
                  >
                    {formatAuditValue(fc.after)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {hiddenCount > 0 && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="mt-2 text-[11px] font-medium text-[hsl(210,100%,38%)] hover:underline"
        >
          Show {hiddenCount} more
        </button>
      )}
    </div>
  );
}

function entityDisplay(event: AuditEvent): {
  text: string;
  isMono: boolean;
} {
  const label = event.entityLabel?.trim();
  if (label && label.toLowerCase() !== "unknown") {
    return { text: label, isMono: false };
  }
  if (event.entityId) return { text: event.entityId.slice(0, 8), isMono: true };
  return { text: "—", isMono: false };
}

type Props = {
  event: AuditEvent;
  isExpanded: boolean;
  onToggle: () => void;
};

export function AuditEventRow({ event, isExpanded, onToggle }: Props) {
  const cat = CATEGORY_STYLES[event.category];
  const isFailure = event.status === "FAILURE";
  const dotColor = actionDotColor(event.action);
  const sevDot = SEVERITY_DOT_COLOR[event.severity];
  const sevLabel = SEVERITY_LABEL[event.severity] ?? event.severity;
  const userName = event.performedByName ?? "System";
  const initials = initialsOf(event.performedByName);
  const rawDescription = event.description?.trim();
  const description =
    rawDescription && !/unknown/i.test(rawDescription)
      ? rawDescription
      : `${event.action.toLowerCase()} on ${formatEntityType(event.entityType).toLowerCase()}`;
  const displayDesc = truncate(description, 60);
  const entity = entityDisplay(event);

  return (
    <>
      <tr className="transition-colors hover:bg-[hsl(210,50%,98%)]">
        <td className="px-3 py-3">
          <button
            type="button"
            onClick={onToggle}
            className="flex h-5 w-5 items-center justify-center rounded hover:bg-[hsl(210,30%,92%)]"
            aria-label="Toggle details"
          >
            {isExpanded ? (
              <ChevronDown className="h-3.5 w-3.5" strokeWidth={1.5} />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" strokeWidth={1.5} />
            )}
          </button>
        </td>
        <td
          className="px-3 py-3 text-xs text-muted-foreground whitespace-nowrap"
          title={relativeTime(event.createdAt)}
        >
          {formatTime(event.createdAt)}
        </td>
        <td className="px-3 py-3">
          <div className="flex items-center gap-2">
            <div
              className="flex h-6 w-6 items-center justify-center rounded-full bg-[hsl(210,55%,92%)] text-[10px] font-semibold text-[hsl(210,100%,28%)]"
              aria-hidden="true"
            >
              {initials}
            </div>
            <span
              className={cn(
                "text-sm font-medium",
                event.performedByName
                  ? "text-foreground"
                  : "text-muted-foreground",
              )}
            >
              {userName}
            </span>
          </div>
        </td>
        <td className="px-3 py-3 text-sm text-foreground" title={description}>
          <div className="flex items-center gap-2">
            <span
              className="inline-block h-2 w-2 shrink-0 rounded-full"
              style={{ background: dotColor }}
            />
            <span className="line-clamp-1">{displayDesc}</span>
          </div>
        </td>
        <td className="px-3 py-3">
          {(() => {
            const formattedModule = formatModule(event.module);
            return formattedModule === "—" ? (
              <span className="text-xs text-muted-foreground">—</span>
            ) : (
              <span
                className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium"
                style={{
                  background: "hsl(210,55%,95%)",
                  color: "hsl(210,100%,32%)",
                }}
              >
                {formattedModule}
              </span>
            );
          })()}
        </td>
        <td className="px-3 py-3">
          {entity.isMono ? (
            <span className="font-mono text-[11px] text-muted-foreground">
              {entity.text}
            </span>
          ) : (
            <span className="text-sm text-foreground">{entity.text}</span>
          )}
        </td>
        <td className="px-3 py-3">
          <span
            className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium"
            style={{ background: cat.bg, color: cat.fg }}
          >
            {cat.label}
          </span>
        </td>
        <td className="px-3 py-3">
          <div className="flex items-center gap-1.5">
            <span
              className="inline-block h-2 w-2 rounded-full"
              style={{ background: sevDot }}
            />
            <span className="text-[11px] text-foreground">{sevLabel}</span>
          </div>
        </td>
      </tr>
      {isExpanded && (
        <tr className="bg-[hsl(210,30%,98%)]">
          <td colSpan={8} className="px-6 py-4">
            <div
              className="rounded-md border bg-card p-4"
              style={{ borderColor: CARD_BORDER }}
            >
              <div className="grid grid-cols-2 gap-3 text-[12px] sm:grid-cols-4">
                <div>
                  <div className="text-[10px] uppercase text-muted-foreground">
                    Event ID
                  </div>
                  <div className="font-mono text-[11px]">{event.id}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-muted-foreground">
                    Timestamp
                  </div>
                  <div className="font-mono text-[11px]">{event.createdAt}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-muted-foreground">
                    Action
                  </div>
                  <div>{event.action}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-muted-foreground">
                    Severity
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span
                      className="inline-block h-2 w-2 rounded-full"
                      style={{ background: sevDot }}
                    />
                    {sevLabel}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-muted-foreground">
                    Entity Type
                  </div>
                  <div>{formatEntityType(event.entityType)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-muted-foreground">
                    Source
                  </div>
                  <div>{event.source}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-muted-foreground">
                    Status
                  </div>
                  <div
                    className={cn(
                      "inline-flex rounded px-1.5 py-0.5 text-[10px] font-medium",
                      isFailure
                        ? "bg-[hsl(0,84%,95%)] text-[hsl(0,72%,40%)]"
                        : "bg-[hsl(142,55%,92%)] text-[hsl(142,72%,28%)]",
                    )}
                  >
                    {event.status}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-muted-foreground">
                    IP / Device
                  </div>
                  <div
                    className="truncate font-mono text-[10px]"
                    title={event.userAgent ?? undefined}
                  >
                    {formatIp(event.ipAddress)}
                  </div>
                </div>
              </div>

              {event.fieldChanges && event.fieldChanges.length > 0 && (
                <FieldChangesTable changes={event.fieldChanges} />
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
